import { LitElement, css, html } from 'lit'
import {
  formatTermDate,
  getSeasonFromStartDate,
} from '../../utils/format-date.js'

import {
  CARD_ICON_URLS,
  getSeasonFallbackIcon,
  getSeasonIconUrl,
  getStatusIcon,
  renderCardIcon,
} from './term-card-icons.js'

/**
 * Karta pojedynczego terminu wyjazdu.
 *
 * Ten komponent nie pobiera danych samodzielnie. Otrzymuje je z zewnątrz
 * (np. datę, cenę i dostępność), a następnie wyświetla je w formie karty.
 * Dzięki temu zmiany wyglądu w tym pliku nie powinny wpływać na API.
 *
 * Dla osoby edytującej wygląd:
 * 1. W sekcji `static styles` znajdują się wszystkie style CSS.
 * 2. W metodzie `render()` znajduje się układ elementów widocznych na karcie.
 * 3. Nie zmieniaj warunku rezerwacji na końcu `render()`, jeśli chcesz
 *    poprawić wyłącznie wygląd.
 */
export class CtTermCard extends LitElement {
  // Lista danych, które można przekazać do komponentu jako atrybuty HTML.
  static properties = {
    // Podstawowe informacje o terminie.
    date: { type: String },
    startDate: {
      type: String,
      attribute: 'start-date',
    },
    endDate: {
      type: String,
      attribute: 'end-date',
    },
    group: { type: String },
    airport: { type: String },
    status: { type: String },

    // Cena główna, np. „4990 zł”.
    price: { type: String },

    // Stały dopisek pod ceną. Może zostać nadpisany przez atrybut flight-price.
    flightPrice: {
      type: String,
      attribute: 'flight-price',
    },

    // Dopisek wyświetlany pod ceną, np. informacja o przedsprzedaży.
    priceNote: {
      type: String,
      attribute: 'price-note',
    },

    // Wariant kolorystyczny statusu dostępności.
    statusVariant: {
      type: String,
      attribute: 'status-variant',
    },

    // Dane odpowiedzialne za przycisk rezerwacji.
    bookingEnabled: {
      type: Boolean,
      attribute: 'booking-enabled',
    },

    bookingUrl: {
      type: String,
      attribute: 'booking-url',
    },

    bookingLabel: {
      type: String,
      attribute: 'booking-label',
    },

    // Imię i zdjęcie pilota wyjazdu.
    pilotName: {
      type: String,
      attribute: 'pilot-name',
    },

    pilotImage: {
      type: String,
      attribute: 'pilot-image',
    },

    // Indywidualne kadrowanie zdjęcia, np. „center 35%”.
    pilotImagePosition: {
      type: String,
      attribute: 'pilot-image-position',
    },

    // Indywidualna wielkość imienia; standardowo karta używa 16px.
    pilotNameFontSize: {
      type: String,
      attribute: 'pilot-name-font-size',
    },

    // Indywidualny odstęp między literami imienia.
    pilotNameLetterSpacing: {
      type: String,
      attribute: 'pilot-name-letter-spacing',
    },

    // Etykieta roli, np. „Pilot wyjazdu” lub „Piloci wyjazdu”.
    pilotLabel: {
      type: String,
      attribute: 'pilot-label',
    },

    // Treści rozwijane przekazywane z danych terminu i profilu pilota.
    expandedDetails: {
      type: Object,
      attribute: false,
    },

    // Wewnętrzny stan rozwijanej sekcji. Nie jest przekazywany z API.
    detailsOpen: { state: true },
    detailsVisible: { state: true },
  }

  constructor() {
    super()

    // Wartości początkowe zapobiegają wyświetlaniu `undefined`, zanim dane
    // zostaną przekazane do komponentu.
    this.date = ''
    this.startDate = ''
    this.endDate = ''
    this.group = ''
    this.airport = ''
    this.status = ''
    this.price = ''
    this.flightPrice = '+ koszt przelotu'
    this.priceNote = ''
    this.statusVariant = 'unknown'
    this.bookingEnabled = false
    this.bookingUrl = ''
    this.bookingLabel = 'Rezerwacja niedostępna'
    this.pilotName = ''
    this.pilotImage = ''
    this.pilotImagePosition = 'center center'
    this.pilotNameFontSize = '16px'
    this.pilotNameLetterSpacing = 'normal'
    this.pilotLabel = 'Pilot wyjazdu'
    this.expandedDetails = {}
    this.detailsOpen = false
    this.detailsVisible = false
    this.detailsAnimating = false
    this.pendingAutomaticClose = false
  }

  // Wszystkie reguły odpowiadające za wygląd karty znajdują się poniżej.
  static styles = css`
    /*
     * SZYBKA ŚCIĄGA DO EDYCJI WYGLĄDU
     * --------------------------------
     * Szerokość całej karty:     :host → max-width
     * Zaokrąglenie karty:        .card → border-radius
     * Wysokość nagłówka:         .date → min-height i padding
     * Wysokość zdjęcia pilota:   .pilot → min-height
     * Odstępy głównych sekcji:   ich właściwości margin i padding
     * Wysokość CTA:              .booking → min-height
     * Widok na małym telefonie:  @media (max-width: 360px)
     *
     * "padding" to odstęp WEWNĄTRZ elementu.
     * "margin" to odstęp NA ZEWNĄTRZ elementu.
     * Zapis "10px 16px" oznacza: 10px góra/dół i 16px lewo/prawo.
     */

    /* 1. Ustawienia całego komponentu */
    :host {
      display: block;
      width: 100%;
      color: #33404c;
      font-family: Poppins, Arial, sans-serif;
    }

    * {
      box-sizing: border-box;
    }

    /* 2. Biała obudowa karty */
    .card {
      overflow: hidden;
      border: 1px solid rgba(24, 88, 106, 0.1);
      border-radius: 18px;
      background: #ffffff;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
      transition: box-shadow 180ms ease;
    }

    /* Na urządzeniu z myszką zmienia się tylko cień — karta nie przesuwa się. */
    @media (hover: hover) {
      .card:hover {
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
      }
    }

    /*
     * KOLORY NAGŁÓWKA DLA CZTERECH PÓR ROKU
     * --------------------------------------
     * Każdy gradient składa się z dwóch kolorów: początku i końca.
     * To jest jedyne miejsce, w którym trzeba je później zmieniać.
     */
    .date--winter {
      --season-color-start: #7698b9;
      --season-color-end: #8ca9c5;
    }

    .date--spring {
      --season-color-start: #6d8d3f;
      --season-color-end: #7f9f46;
    }

    .date--summer {
      --season-color-start: #b77932;
      --season-color-end: #cf9544;
    }

    .date--autumn {
      --season-color-start: #a86543;
      --season-color-end: #c77b58;
    }

    /* 3. Nagłówek z datą i gradientem dobranym do pory roku */
    .date {
      display: flex;
      /* Zmień tę wartość, aby nagłówek był wyższy lub niższy. */
      min-height: 56px;
      align-items: center;
      gap: 12px;
      padding: 8px 12px 6px;
      background:
        radial-gradient(
          circle at 88% 15%,
          rgba(255, 255, 255, 0.22),
          transparent 34%
        ),
        linear-gradient(
          135deg,
          var(--season-color-start, #7698b9),
          var(--season-color-end, #8ca9c5)
        );
      color: #ffffff;
    }

    /* Ikona sezonu bez dodatkowego tła i obramowania. */
    .season-icon {
      display: grid;
      flex: 0 0 auto;
      width: 42px;
      height: 42px;
      place-items: center;
    }

    .season-icon svg,
    .season-icon img {
      width: 35px;
      height: 35px;
      object-fit: contain;
    }

    .date-copy {
      display: flex;
      min-width: 0;
      flex-direction: column;
      justify-content: center;
    }

    /* Mały napis „Termin wyjazdu”. */
    .eyebrow {
      display: block;
      margin-bottom: -1px;
      color: rgba(255, 255, 255, 0.78);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.13em;
      text-transform: uppercase;
    }

    .date-value {
      display: block;
      font-size: clamp(19px, 5.5vw, 23px);
      font-weight: 500;
      line-height: 1.25;
    }

    /* 4. Sekcja zdjęcia pilota oraz etykiet „Grupa” i „Wylot” */
    .summary {
      display: grid;
      grid-template-columns: 108px minmax(0, 1fr);
      gap: 10px;
      padding: 16px 0 8px;
    }

    /* Lewa kolumna z okrągłym zdjęciem i podpisem na zdjęciu. */
    .pilot {
      display: grid;
      min-height: 108px;
      place-items: center start;
      color: #667681;
    }

    /* Prostokątne zdjęcie dosunięte bezpośrednio do lewej krawędzi karty. */
    .pilot-avatar {
      position: relative;
      width: 108px;
      height: 108px;
      overflow: hidden;
      border: 0;
      border-radius: 0 14px 14px 0;
      background: linear-gradient(145deg, #e6edf1, #f7f9fa);
    }

    /* Zdjęcie wypełnia koło bez zmiany swoich proporcji. */
    .pilot img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /* Tekst zastępczy widoczny, gdy nie przekazano zdjęcia pilota. */
    .pilot-placeholder {
      display: grid;
      width: 100%;
      height: 100%;
      place-items: center;
      padding: 8px;
      text-align: center;
      font-size: 9px;
      line-height: 1.25;
    }

    /* Gradient zwiększa czytelność imienia i etykiety na zdjęciu. */
    .pilot-copy {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      padding: 28px 4px 7px;
      background: linear-gradient(transparent, rgba(24, 42, 50, 0.88));
      text-align: center;
    }

    .pilot-label {
      display: block;
      margin-top: 1px;
      color: rgba(255, 255, 255, 0.75);
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.07em;
      line-height: .9;
      text-transform: uppercase;
      white-space: nowrap;
    }

    .pilot-name {
      display: block;
      overflow-wrap: anywhere;
      color: #ffffff;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.6;
    }

    /* Prawa kolumna zawierająca obie etykiety informacyjne. */
    .meta {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: 8px;
    }

    /* Pojedyncza etykieta, np. „Grupa: 12 osób”. */
    .pill {
      display: grid;
      flex: 1 1 0;
      grid-template-columns: 34px minmax(0, 1fr);
      min-width: 0;
      align-items: center;
      gap: 7px;
      padding: 4px;
      border: 1px solid #eeeee4;
      border-right: 0;
      border-radius: 14px 0 0 14px;
    }

    /* Ikona wewnątrz etykiety — bez dodatkowego tła i obramowania. */
    .pill-icon {
      display: grid;
      width: 32px;
      height: 32px;
      place-items: center;
      color: #18586a;
    }

    .pill-icon svg,
    .pill-icon img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }

    .pill-label {
      display: block;
      margin-bottom: 0;
      color: #8a969d;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.07em;
      text-transform: uppercase;
    }

    /* Właściwa wartość etykiety, np. nazwa grupy lub lotniska. */
    .pill-value {
      display: block;
      overflow-wrap: anywhere;
      color: #33404c;
      font-size: 15px;
      font-weight: 500;
      line-height: 1.35;
    }

    /* 5. Pasek prowadzący do programu i szczegółów terminu */
    .details {
      display: flex;
      width: calc(100% - 24px);
      min-height: 42px;
      margin: 0 12px 4px;
      align-items: center;
      gap: 9px;
      padding: 7px 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      color: #1b4e5c;
      text-align: left;
      font: inherit;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: color 160ms ease;
    }

    .details:hover {
      color: #174854;
    }

    .details:focus-visible {
      outline: 3px solid rgba(24, 88, 106, 0.24);
      outline-offset: 2px;
    }

    .details svg,
    .details img {
      width: 22px;
      height: 22px;
      object-fit: contain;
    }

    .details .arrow {
      margin-left: auto;
      color: #819fbe;
      width: 32px;
      height: 32px;
      transition: transform 180ms ease;
    }

    /* Po rozwinięciu strzałka obraca się w górę. */
    .details[aria-expanded='true'] .arrow {
      transform: rotate(180deg);
    }

    /*
     * Panel od razu zajmuje docelową wysokość. Animowana jest wyłącznie jego
     * treść, dlatego WebWave przelicza pozycje elementów poniżej tylko raz.
     */
    .term-details-panel {
      display: none;
    }

    .term-details-panel--visible {
      display: block;
    }

    /* Ten element ukrywa treść podczas zmniejszania wysokości panelu. */
    .term-details-clip {
      min-height: 0;
      overflow: hidden;
      will-change: transform, opacity;
    }

    /* Właściwy blok dodatkowych informacji o terminie. */
    .term-details {
      margin: 0 12px 12px;
      padding: 0;
      border: 1px solid #e4ecef;
      border-right: 0;
      border-left: 0;
      border-radius: 0;
      background: #ffffff;
    }

    .term-detail-section {
      position: relative;
      padding: 8px 0;
    }

    /* Krótsza, wyśrodkowana linia między sekcjami szczegółów. */
    .term-detail-section:not(:last-child)::after {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 100px;
      height: 1px;
      background: #e8edef;
      transform: translateX(-50%);
      content: '';
}

    /* Ikona i tytuł są w jednym wierszu; opis poniżej ma pełną szerokość. */
    .term-detail-heading {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }

    /* Niewielka ikona bez tła i obramowania. */
    .term-detail-icon {
      display: grid;
      width: 28px;
      height: 28px;
      place-items: center;
    }

    /* Każda kategoria ma własny, stonowany kolor ułatwiający skanowanie. */
    .term-detail-section--flight .term-detail-icon {
      color: #517fa3;
    }

    .term-detail-section--hotel .term-detail-icon {
      color: #9a754a;
    }

    .term-detail-section--pilot .term-detail-icon {
      color: #4f8462;
    }

    .term-detail-icon svg,
    .term-detail-icon img {
      width: 22px;
      height: 22px;
      object-fit: contain;
    }

    .term-detail-title {
      margin: 0;
      color: #404040;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.35;
    }

    .term-detail-description {
      margin: 0;
      padding: 0 4px;
      color: #5e5e5e;
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 15px;
      line-height: 1.5;
    }

    /* Osoby ograniczające animacje w systemie otrzymają zmianę bez ruchu. */
    @media (prefers-reduced-motion: reduce) {
      .arrow {
        transition: none;
      }
    }

    /* 6. Pasek statusu dostępności */
    .status {
      display: flex;
      width: 100%;
      margin: 0;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 11px;
      border-radius: 0;
      text-align: center;
      text-transform: uppercase;
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 0.03em;
    }

    /* Wielkość ikon statusu — wspólna dla ikon własnych i awaryjnych. */
    .status-icon {
      display: grid;
      flex: 0 0 auto;
      width: 24px;
      height: 24px;
      place-items: center;
    }

    .status-icon svg,
    .status-icon img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }

    /* Zielony: termin dostępny. */
    .status--available {
      color: #48a158;
      background: #edf8ef;
    }

    /* Pomarańczowy: zostało tylko kilka miejsc. */
    .status--last-places {
      color: #c97323;
      background: #fff3e6;
    }

    /* Czerwony: brak wolnych miejsc. */
    .status--sold-out {
      color: #bd3434;
      background: #fceded;
    }

    /* Szary wariant awaryjny, gdy status nie został rozpoznany. */
    .status--unknown {
      color: #68737a;
      background: #eef0f1;
    }

    /* 7. Cena, dodatkowe informacje cenowe i przycisk rezerwacji */
    .price-area {
      padding: 13px 12px 8px;
    }

    /* Cena po lewej i CTA po prawej tworzą jeden zwarty wiersz. */
    .purchase-row {
      display: grid;
      grid-template-columns: auto minmax(145px, 1fr);
      min-width: 0;
      align-items: stretch;
      gap: 22px;
    }

    .price-summary {
      display: flex;
      min-width: 0;
      flex-direction: column;
      justify-content: center;
      letter-spacing: -0.01em;
      transform: translateY(2px);
    }

    .price {
      color: #398248;
      font-size: 24px;
      font-weight: 600;
      line-height: 1.1;
    }

    /* Slash jest celowo mniejszy niż cena i dopisek „os.”. */
    .price-separator {
      display: inline-block;
      margin: 0 2px;
      font-size: 0.8em;
      font-weight: 600;
      line-height: 1;
      vertical-align: 0.02em;
    }

    /* Opcjonalna informacja o dopłacie lub cenie lotu. */
    .flight-price {
      color: #5e5e5e;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.25;
      letter-spacing: 0.02em;
    }

    /* Opcjonalny dopisek, np. warunki ceny przedsprzedażowej. */
    .price-note {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #edf0f2;
      color: #6b7280;
      font-size: 13px;
      line-height: 1;
      text-align: center;
    }

    /*
     * Pomarańczowy przycisk CTA.
     * Ten sam styl otrzymuje aktywny link i nieaktywny przycisk.
     */
    .booking {
      display: flex;
      width: 100%;
      min-width: 0;
      min-height: 46px;
      margin-top: 0;
      align-items: center;
      justify-content: center;
      padding: 8px 10px;
      border: 0;
      border-radius: 12px;
      background: #f7931e;
      color: #ffffff;
      text-decoration: none;
      text-transform: uppercase;
      font: inherit;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0.025em;
      line-height: 1.2;
      white-space: nowrap;
      cursor: pointer;
      transition:
        background 160ms ease,
        box-shadow 160ms ease,
        transform 160ms ease;
    }

    /* Efekt najechania dotyczy tylko CTA, nie całej karty. */
    .booking:hover {
      background: #ff9f2e;
      transform: translateY(-1px);
    }

    .booking:focus-visible {
      outline: 3px solid rgba(24, 88, 106, 0.28);
      outline-offset: 3px;
    }

    .booking:disabled {
      background: #b8c0c4;
      box-shadow: none;
      cursor: not-allowed;
      transform: none;
    }

    /* 8. Dodatkowe zagęszczenie układu na bardzo wąskich telefonach */
    @media (max-width: 360px) {
      .summary {
        grid-template-columns: 108px minmax(0, 1fr);
        gap: 10px;
        padding: 12px 0;
      }

      .pilot {
        min-height: 108px;
      }

      .pilot-avatar {
        width: 108px;
        height: 108px;
      }

      /* Pastylki zachowują jeden wiersz dzięki mniejszym ikonom i odstępom. */
      .pill {
        grid-template-columns: 28px minmax(0, 1fr);
        gap: 4px;
        padding: 3px;
      }

      .pill-icon {
        width: 28px;
        height: 28px;
      }

      .pill-icon svg,
      .pill-icon img {
        width: 20px;
        height: 20px;
      }

      .pill-label {
        font-size: 9px;
      }

      .pill-value {
        font-size: clamp(12px, 3.8vw, 13px);
        letter-spacing: -0.01em;
        line-height: 1.25;
        white-space: nowrap;
      }

      .details {
        margin-right: 12px;
        margin-left: 12px;
      }

      .details {
        width: calc(100% - 24px);
      }

      .term-details {
        margin-right: 12px;
        margin-left: 12px;
      }

      .price-area {
        padding-right: 12px;
        padding-left: 12px;
      }

      /* Mniejsza kolumna ceny pozostawia więcej miejsca na dłuższe CTA. */
      .purchase-row {
        grid-template-columns: auto minmax(0, 1fr);
        gap: 10px;
      }

      .price {
        font-size: 21px;
      }

      .flight-price {
        font-size: 12px;
        white-space: nowrap;
      }

      .booking {
        padding-right: 6px;
        padding-left: 6px;
        letter-spacing: 0.01em;
      }

      .price-note {
        font-size: 11px;
        line-height: 1.1;
      }
    }
  `

  // Otwiera lub zamyka dodatkowe informacje po kliknięciu w pasek szczegółów.
  async toggleDetails() {
    if (this.detailsAnimating) return

    const shouldOpen = !this.detailsOpen

    if (shouldOpen) {
      // Kontener ct-offer-terms zamknie pozostałe otwarte karty.
      this.dispatchEvent(
        new CustomEvent('ct-term-details-open', {
          bubbles: true,
          composed: true,
          detail: { card: this },
        })
      )
    }

    await this.setDetailsOpen(shouldOpen)
  }

  /**
   * Zamyka szczegóły na polecenie kontenera, gdy użytkownik otworzy inną kartę.
   * Jeśli trwa właśnie otwieranie, zamknięcie wykona się zaraz po jego końcu.
   */
  async closeDetails() {
    if (!this.detailsOpen && !this.detailsVisible) {
      return
    }

    if (this.detailsAnimating) {
      this.pendingAutomaticClose = true
      return
    }

    await this.setDetailsOpen(false)
  }

  // Wspólna animacja używana przy ręcznym i automatycznym zamykaniu.
  async setDetailsOpen(shouldOpen) {
    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (prefersReducedMotion) {
      this.detailsOpen = shouldOpen
      this.detailsVisible = shouldOpen
      return
    }

    this.detailsAnimating = true

    try {
      if (shouldOpen) {
        // Najpierw zajmujemy pełną wysokość i pozwalamy WebWave wykonać skok.
        this.detailsOpen = true
        this.detailsVisible = true
        await this.updateComplete
      } else {
        // Podczas zanikania miejsce pozostaje zarezerwowane do końca animacji.
        this.detailsOpen = false
        await this.updateComplete
      }

      const clip = this.renderRoot.querySelector('.term-details-clip')

      if (!clip?.animate) {
        this.detailsVisible = shouldOpen
        return
      }

      const keyframes = shouldOpen
        ? [
            { opacity: 0, transform: 'translateY(-8px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ]
        : [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: 'translateY(-6px)' },
          ]

      const animation = clip.animate(keyframes, {
        duration: shouldOpen ? 180 : 140,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
        fill: 'both',
      })

      await animation.finished.catch(() => {})

      if (!shouldOpen) {
        // Dopiero teraz zwalniamy wysokość — WebWave przesuwa elementy raz.
        this.detailsVisible = false
        await this.updateComplete
      }

      animation.cancel()
    } finally {
      this.detailsAnimating = false

      if (
        this.pendingAutomaticClose &&
        this.detailsOpen
      ) {
        this.pendingAutomaticClose = false
        void this.closeDetails()
      } else {
        this.pendingAutomaticClose = false
      }
    }
  }

  /**
   * Buduje zawartość widoczną na karcie.
   *
   * Zapisy `${this.nazwa}` wstawiają dane przekazane do komponentu.
   * Nie zastępuj ich stałym tekstem, jeśli karta ma nadal pokazywać
  * różne informacje dla każdego terminu.
  */
  render() {
    const season = getSeasonFromStartDate(this.startDate)
    const formattedDate = formatTermDate(
      this.startDate,
      this.endDate,
      this.date,
    )
    const [priceMain, priceUnit = ''] = String(this.price)
      .split(/\s*\/\s*/, 2)
    const details = this.expandedDetails ?? {}
    const detailSections = [
      {
        key: 'flight',
        content: details.flight,
        iconUrl: CARD_ICON_URLS.flight,
        fallbackIcon: html`
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="m17.8 19-1.8-8 3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 5 6 3 8l6.5 3.5L6 15H3l-1 1 4 2 2 4 1-1v-3l3.5-3.5L16 21Z" />
          </svg>
        `,
      },
      {
        key: 'hotel',
        content: details.hotel,
        iconUrl: CARD_ICON_URLS.hotel,
        fallbackIcon: html`
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M4 21V5l8-3 8 3v16M8 9h2M14 9h2M8 13h2M14 13h2M9 21v-4h6v4M2 21h20" />
          </svg>
        `,
      },
      {
        key: 'pilot',
        content: details.pilot,
        iconUrl: CARD_ICON_URLS.pilot,
        fallbackIcon: html`
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
          </svg>
        `,
      },
    ].filter(section =>
      Boolean(section.content?.title || section.content?.description)
    )

    return html`
      <article class="card">
        <!-- NAGŁÓWEK: ikona sezonu i data wyjazdu -->
        <header class="date date--${season}">
          <span class="season-icon" aria-hidden="true">
            ${renderCardIcon(
              getSeasonIconUrl(season),
              getSeasonFallbackIcon(season),
            )}
          </span>

          <span class="date-copy">
            <span class="eyebrow">Termin wyjazdu</span>
            <span class="date-value">${formattedDate}</span>
          </span>
        </header>

        <!-- PODSUMOWANIE: pilot, grupa oraz miejsce wylotu -->
        <section class="summary">
          <div class="pilot">
            <div class="pilot-avatar">
              <!-- Jeśli istnieje zdjęcie, pokaż je; w przeciwnym razie pokaż tekst. -->
              ${this.pilotImage
                ? html`
                    <img
                      src=${this.pilotImage}
                      style=${`object-position: ${this.pilotImagePosition};`}
                      alt=${this.pilotName
                        ? `${this.pilotLabel} – ${this.pilotName}`
                        : this.pilotLabel}
                    >
                  `
                : html`
                    <span class="pilot-placeholder">Zdjęcie wkrótce</span>
                  `}

              <div class="pilot-copy">
                ${this.pilotName
                  ? html`
                      <span
                        class="pilot-name"
                        style=${`
                          font-size: ${this.pilotNameFontSize};
                          letter-spacing: ${this.pilotNameLetterSpacing};
                        `}
                      >${this.pilotName}</span>
                    `
                  : ''}
                <span class="pilot-label">${this.pilotLabel}</span>
              </div>
            </div>
          </div>

          <div class="meta">
            <div class="pill">
              <span class="pill-icon" aria-hidden="true">
                ${renderCardIcon(
                  CARD_ICON_URLS.group,
                  html`
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  `,
                )}
              </span>
              <span>
                <span class="pill-label">Grupa</span>
                <span class="pill-value">${this.group}</span>
              </span>
            </div>

            <div class="pill">
              <span class="pill-icon" aria-hidden="true">
                ${renderCardIcon(
                  CARD_ICON_URLS.airport,
                  html`
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                      <path d="m17.8 19-1.8-8 3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 5 6 3 8l6.5 3.5L6 15H3l-1 1 4 2 2 4 1-1v-3l3.5-3.5L16 21Z" />
                    </svg>
                  `,
                )}
              </span>
              <span>
                <span class="pill-label">Wylot</span>
                <span class="pill-value">${this.airport}</span>
              </span>
            </div>
          </div>
        </section>

        <!-- Klikalny pasek rozwijający dodatkowe informacje o terminie. -->
        <button
          class="details"
          type="button"
          aria-expanded=${this.detailsOpen}
          aria-controls="term-details"
          @click=${this.toggleDetails}
        >
          ${renderCardIcon(
            CARD_ICON_URLS.details,
            html`
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
            `,
          )}
          <span>Sprawdź szczegóły terminu</span>
          ${renderCardIcon(
            CARD_ICON_URLS.arrow,
            html`
              <svg class="arrow" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m6 9 6 6 6-6" />
              </svg>
            `,
            'arrow',
          )}
        </button>

        <div
          class="term-details-panel ${this.detailsVisible
            ? 'term-details-panel--visible'
            : ''}"
          id="term-details"
          aria-hidden=${!this.detailsOpen}
        >
          <div class="term-details-clip">
            <div class="term-details">
              ${detailSections.map(
                section => html`
                  <section class="term-detail-section term-detail-section--${section.key}">
                    <div class="term-detail-heading">
                      <span class="term-detail-icon" aria-hidden="true">
                        ${renderCardIcon(
                          section.iconUrl,
                          section.fallbackIcon,
                        )}
                      </span>

                      ${section.content.title
                        ? html`
                            <h3 class="term-detail-title">
                              ${section.content.title}
                            </h3>
                          `
                        : ''}
                    </div>

                    ${section.content.description
                      ? html`
                          <p class="term-detail-description">
                            ${section.content.description}
                          </p>
                        `
                      : ''}
                  </section>
                `
              )}
            </div>
          </div>
        </div>

        <!-- Tekst i wariant statusu pochodzą z get-availability.js. -->
        <div class="status status--${this.statusVariant}">
          ${this.statusVariant !== 'unknown'
            ? html`
                <span class="status-icon" aria-hidden="true">
                  ${getStatusIcon(this.statusVariant)}
                </span>
              `
            : ''}
          <span>${this.status}</span>
        </div>

        <!-- CENA: kwota główna, informacje dodatkowe oraz CTA -->
        <section class="price-area">
          <div class="purchase-row">
            <div class="price-summary">
              <div class="price">
                <span>${priceMain}</span>${priceUnit
                  ? html`<span class="price-separator">/</span><span>${priceUnit}</span>`
                  : ''}
              </div>

              ${this.flightPrice
                ? html`<div class="flight-price">${this.flightPrice}</div>`
                : ''}
            </div>

            <!--
              WAŻNE: poniższy warunek kontroluje możliwość rezerwacji.
              Link jest pokazywany tylko wtedy, gdy rezerwacja jest włączona
              i istnieje jej adres. W innym przypadku widoczny jest przycisk
              nieaktywny. To logika działania, a nie wyłącznie wygląd.
            -->
            ${this.bookingEnabled && this.bookingUrl
              ? html`
                  <a class="booking" href=${this.bookingUrl}>
                    ${this.bookingLabel || 'Rezerwuj'}
                  </a>
                `
              : html`
                  <button class="booking" type="button" disabled>
                    ${this.bookingLabel}
                  </button>
                `}
          </div>

          ${this.priceNote
            ? html`<div class="price-note">${this.priceNote}</div>`
            : ''}
        </section>
      </article>
    `
  }
}

// Rejestracja komponentu. Sprawdzenie zapobiega ponownej rejestracji tej samej nazwy.
if (!customElements.get('ct-term-card')) {
  customElements.define('ct-term-card', CtTermCard)
}
